<template>
  <div class="excel-test">
    <span v-if="wsStatus" style="margin-left:10px;">{{ wsStatus }}</span>
    <header class="detail-header">
      <button @click="goBack" class="back-btn">← 돌아가기</button>
      <h1>📊 Excel 테스트</h1>
      <div class="header-actions">
        <!-- === 카메라 선택 === -->
          <label class="text-sm font-medium">카메라 선택</label>
          <div class="flex gap-2 items-center mb-3">
            <select v-model="selectedDeviceId" class="border rounded px-2 py-1 w-full">
              <option v-for="d in devices" :key="d.deviceId" :value="d.deviceId">
                {{ d.label || 'Camera' }}
              </option>
            </select>
            <button
              class="px-3 py-2 bg-gray-100 rounded"
              @click="restartWithDevice"
              :disabled="!selectedDeviceId"
            >
              변경
            </button>
          </div>
          <!-- === /카메라 선택 === -->
          
        <div
          class="ws-indicator"
          :class="{ on: wsConnected }"
          :title="wsConnected ? '서버 연결됨' : '서버 해제됨'"
        >
          <span class="dot"></span>
          <span class="label">{{ wsConnected ? '연결됨' : '해제됨' }}</span>
        </div>
        <button
          @click="wsConnected ? disconnectFromServer() : connectToServer()"
          class="connect-btn"
        >
          {{ wsConnected ? '연결 해제' : '서버 연결' }}
        </button>
      </div>
    </header>

    <main class="detail-content">
      <!-- 좌측: Excel 파일 업로드 영역 -->
      <div class="upload-section">
        <div class="upload-container">
          <h2>📁 Excel 파일 업로드</h2>
          
          <!-- 파일 드래그 앤 드롭 영역 -->
          <div 
            class="upload-area" 
            @drop="handleDrop" 
            @dragover.prevent 
            @dragenter.prevent
            :class="{ 'drag-over': isDragOver }"
            @dragenter="isDragOver = true"
            @dragleave="isDragOver = false"
          >
            <input 
              ref="fileInput"
              type="file" 
              @change="handleFileSelect" 
              accept=".xlsx,.xls,.csv"
              class="file-input"
              id="excel-file"
            >
            <label for="excel-file" class="upload-label">
              <div class="upload-icon">📁</div>
              <h3>Excel 파일을 선택하거나 드래그하여 업로드하세요</h3>
              <p class="upload-hint">지원 형식: .xlsx, .xls, .csv</p>
              <div class="upload-button">파일 선택</div>
            </label>
          </div>
          
          <!-- 텍스트 입력 영역 -->
          <div class="message-input-section">
            <label for="file-message" class="message-label">📝 파일 설명 (선택사항)</label>
            <textarea 
              id="file-message"
              v-model="fileMessage"
              placeholder="파일에 대한 설명이나 메모를 입력하세요..."
              class="message-input"
              rows="3"
              :disabled="uploading"
            ></textarea>
            <div class="message-hint">이 메시지는 파일과 함께 전송됩니다.</div>
          </div>
          
          <!-- 업로드 컨트롤 버튼 (항상 표시) -->
          <div class="upload-controls-fixed">
            <button 
              @click="processAnalysisData" 
              :disabled="uploading || !selectedFile"
              class="upload-btn"
            >
              {{ uploading ? '전송 중...' : '📤 파일 전송하기' }}
            </button>
            <button 
              v-if="selectedFile"
              @click="clearFile" 
              class="clear-btn"
              :disabled="uploading"
            >
              🗑️ 파일 제거
            </button>
          </div>
          
          <!-- 업로드 진행률 -->
          <div v-if="uploadProgress > 0" class="progress-container">
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :style="{ width: uploadProgress + '%' }"
              ></div>
            </div>
            <span class="progress-text">{{ Math.round(uploadProgress) }}%</span>
          </div>
          
          <!-- 선택된 파일 정보 -->
          <div v-if="selectedFile" class="file-info">
            <h3>📄 선택된 파일 정보</h3>
            <div class="file-details">
              <p><strong>파일명:</strong> {{ selectedFile.name }}</p>
              <p><strong>크기:</strong> {{ formatFileSize(selectedFile.size) }}</p>
              <p><strong>타입:</strong> {{ selectedFile.type || '알 수 없음' }}</p>
              <div class="info-item">
                <strong>Rag 정보:</strong> {{ imageData.name || '없음' }}
                  <button
                    v-if="imageData.name"
                    @click="playRagIdTTS"
                    class="tts-btn"
                    style="margin-left:6px"
                  >
                    🔊 읽어주기
                  </button>
              </div>
              
             
            </div>
          </div>
          
          <!-- 업로드 결과 -->
          <div v-if="uploadResult" class="upload-result success">
            <h4>✅ 업로드 성공!</h4>
            <p>{{ uploadResult }}</p>
          </div>
          
          <div v-if="uploadError" class="upload-result error">
            <h4>❌ 업로드 실패</h4>
            <p>{{ uploadError }}</p>
          </div>
        </div>
      </div>

      <!-- 우측: 분석 및 대화 영역 -->
      <div class="analysis-section">
        <!-- 이미지 분석 결과 -->
        <div class="analysis-panel">
        <!--
          <h3>📊 이미지 분석 결과</h3>
          -->
          
          <!-- TTS 속도 선택 + 모델 선택 좌우배치 컨테이너 -->
          <div class="controls-horizontal-container">
            <!-- TTS 속도 선택 영역 -->
            <div class="tts-rate-wrapper">
              <h4 class="tts-rate-title">🔊 음성 속도 설정</h4>
              <div class="tts-rate-container">
                <div class="rate-label">속도:</div>
                <div class="rate-buttons-wrapper">
                  <button 
                    v-for="num in [2,5,8,12,15]" 
                    :key="num"
                    class="rate-btn"
                    :class="{ active: rate === num }"
                    @click="selectRate(num)"
                  >
                    {{ num }}x
                  </button>
                </div>
              </div>
            </div>
            
            <!-- 모델 선택 영역 -->
            <div class="model-selection-wrapper">
              <h4 class="model-selection-title">🤖 모델 선택</h4>
              <div class="model-toggle-container">
                <div class="toggle-label">Model Type:</div>
                <div class="toggle-switch-wrapper">
                  <button 
                    class="toggle-option"
                    :class="{ active: ours }"
                    @click="selectOurs"
                  >
                    Ours
                  </button>
                  <button 
                    class="toggle-option"
                    :class="{ active: !ours }"
                    @click="selectBaseline"
                  >
                    Baseline
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 타이머 전용 영역 -->
          <div class="timer-section-wrapper">
            <h4 class="timer-section-title">⏱️ 측정 타이머</h4>
            <div class="timer-container">
    <!-- 탐색 타이머 -->
    <div class="timer-section">
      <div class="timer-label">탐색</div>
      <div class="timer-display">
        <div class="timer-icon">🔍</div>
        <div class="timer-time">{{ formattedTime0 }}</div>
        <div class="timer-status" :class="{ running: timer0Running }"></div>
      </div>
      <div class="timer-controls">
        <button 
          @click="startTimer0" 
          class="timer-btn start-btn"
          :disabled="timer0Running"
        >
          시작
        </button>
        <button 
          @click="stopTimer0" 
          class="timer-btn stop-btn"
          :disabled="!timer0Running"
        >
          종료
        </button>
        <button 
          @click="resetTimer0" 
          class="timer-btn reset-btn"
          :disabled="timer0Running || elapsed0Ms === 0"
        >
          초기화
        </button>
      </div>
    </div>

    <!-- 타이머 1 -->
    <div class="timer-section">
      <div class="timer-label">문제 1</div>
      <div class="timer-display">
        <div class="timer-icon">⏱️</div>
        <div class="timer-time">{{ formattedTime1 }}</div>
        <div class="timer-status" :class="{ running: timer1Running }"></div>
      </div>
      <div class="timer-controls">
        <button 
          @click="startTimer1" 
          class="timer-btn start-btn"
          :disabled="timer1Running"
        >
          시작
        </button>
        <button 
          @click="stopAndSendTimer1" 
          class="timer-btn record-btn"
          :disabled="!timer1Running"
        >
          기록
        </button>
        <button 
          @click="stopTimer1" 
          class="timer-btn stop-btn"
          :disabled="!timer1Running"
        >
          종료
        </button>
        <button 
          @click="resetTimer1" 
          class="timer-btn reset-btn"
          :disabled="timer1Running || elapsed1Ms === 0"
        >
          초기화
        </button>
      </div>
    </div>

    <!-- 타이머 2 -->
    <div class="timer-section">
      <div class="timer-label">문제 2</div>
      <div class="timer-display">
        <div class="timer-icon">⏱️</div>
        <div class="timer-time">{{ formattedTime2 }}</div>
        <div class="timer-status" :class="{ running: timer2Running }"></div>
      </div>
      <div class="timer-controls">
        <button 
          @click="startTimer2" 
          class="timer-btn start-btn"
          :disabled="timer2Running"
        >
          시작
        </button>
        <button 
          @click="stopAndSendTimer2" 
          class="timer-btn record-btn"
          :disabled="!timer2Running"
        >
          기록
        </button>
        <button 
          @click="stopTimer2" 
          class="timer-btn stop-btn"
          :disabled="!timer2Running"
        >
          종료
        </button>
        <button 
          @click="resetTimer2" 
          class="timer-btn reset-btn"
          :disabled="timer2Running || elapsed2Ms === 0"
        >
          초기화
        </button>
      </div>
    </div>
            </div>
          </div>



          
          <div class="analysis-content">
            <div v-if="analysisLoading" class="loading">
              분석 중...
            </div>
            <div v-else class="analysis-results">
              
              <!-- 분석 결과 표시 -->
              <div v-if="imageData.isAnalyzed && imageData.analyzed" class="ai-analysis">
                <h4>🤖 AI 분석 결과</h4>
                <button @click="playAnalysisTTS" class="tts-btn">
                  🔊 읽어주기
                </button>
                <div class="analysis-json-container">
                  <button @click="toggleJsonExpanded" class="json-toggle-btn">
                    {{ jsonExpanded ? '접기' : '전체 보기' }}
                  </button>
                  <pre class="analysis-json" :class="{ expanded: jsonExpanded }">{{ formatAnalysisData(imageData.analyzed) }}</pre>
                </div>
              </div>
              
              <!-- 기존 분석 결과 (호환성) -->
              <div v-if="detectedObjects.length > 0" class="analysis-item">
                <strong>감지된 객체:</strong>
                <ul>
                  <li v-for="object in detectedObjects" :key="object">{{ object }}</li>
                </ul>
              </div>
              <div v-if="dominantColors.length > 0" class="analysis-item">
                <strong>색상 분석:</strong>
                <div class="color-palette">
                  <div 
                    v-for="color in dominantColors" 
                    :key="color"
                    class="color-swatch"
                    :style="{ backgroundColor: color }"
                    :title="color"
                  ></div>
                </div>
              </div>
              <div v-if="imageCharacteristics" class="analysis-item">
                <strong>특성 분석:</strong>
                <p>{{ imageCharacteristics }}
                  <button v-if="imageCharacteristics" @click="playAnalysisTTS" class="tts-btn">🔊</button>
                </p>
              </div>
            </div>
          </div>

          <!-- === WS 스트림 / ROI 미리보기 === -->
          <div class="ws-row">
            <div class="ws-col">
              <h4 class="ws-title">🎥 실시간 스트림</h4>
              <div class="ws-frame">
                <img v-if="wsFrameSrc" :src="wsFrameSrc" class="ws-img" alt="stream" />
                <div v-else class="ws-empty">스트림 대기중…</div>
                <div class="ws-fps">FPS: {{ wsFps }}</div>
              </div>
              <div class="ws-stats">
                <span>연결: <b>{{ wsConnected ? '✅' : '❌' }}</b></span>
                <span>서버프레임: {{ wsServerFrame }}</span>
                <span>ArUco: {{ wsAruco ? '✅' : '❌' }}</span>
                <span>손: {{ wsHand ? '✅' : '❌' }}</span>
              </div>
            </div>

            <div class="ws-col">
              <h4 class="ws-title">📸 저장된 ROI</h4>
              <div class="ws-frame">
                <img v-if="wsSavedRoiSrc" :src="wsSavedRoiSrc" class="ws-img" alt="roi" />
                <div v-else class="ws-empty">아직 저장된 ROI 없음</div>
              </div>
              <div class="ws-stats">
                <span>상태: {{ wsSaveStatus }}</span>
                <span v-if="wsSavedRoiSize">크기: {{ wsSavedRoiSize }}</span>
              </div>
              <div style="margin-top:8px; display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
                <button class="status-btn" @click="sendTipToApi(wsSavedRoiSrc)" :disabled="!wsSavedRoiSrc || apiSending">
                  {{ apiSending ? '전송 중...' : 'API로 전송' }}
                </button>
                <span v-if="apiResult" style="color:#2d5016;">✅ {{ apiResult }}</span>
                <span v-if="apiError" style="color:#c62828;">❌ {{ apiError }}</span>
              </div>
            </div>
          </div>
          <!-- === /WS 스트림 / ROI 미리보기 === -->


        </div>

        <!-- 대화 창 -->
        <div class="chat-panel">
          <div class="chat-panel-header">
            <h3>💬 Excel 데이터에 대해 질문하기</h3>
            <button @click="toggleTTS" class="tts-toggle-btn">
              {{ ttsEnabled ? '🔇 음성 Off 🔇' : '🔊 음성 On 🔊' }}
            </button>
          </div>
          

          
          <div v-if="isListening" class="stt-indicator">🎤 음성 인식 중... (Ctrl+Shift를 떼면 음성 인식이 완료됩니다)</div>
          <div v-else class="stt-indicator">Ctrl+Shift를 누르면 음성 인식이 시작됩니다</div>
          <div v-if="sttError" class="error">{{ sttError }}</div>
          <div class="chat-messages" ref="chatMessages">
            <div v-if="chatMessages.length === 0" class="empty-chat-message">
              👋 안녕하세요! Excel 파일을 업로드한 후 데이터에 대해 궁금한 것을 질문해보세요.<br>
            </div>
            <div 
              v-for="message in chatMessages" 
              :key="message.id"
              :class="['message', message.type]"
            >
              <div class="message-content">
                {{ message.content }}
                <button v-if="message.type === 'assistant' && message.content" @click="playChatTTS(message)" class="tts-btn">🔊</button>
              </div>
              <div class="message-time">
                {{ formatTime(message.timestamp) }}
              </div>
            </div>
          </div>
          
          <div class="chat-input-area">
            <div class="input-container">
              <input
                v-model="currentMessage"
                @keyup.enter="sendMessage"
                placeholder="Excel 데이터에 대해 질문해보세요..."
                class="chat-input"
                :disabled="chatLoading"
              >
              <button 
                @click="sendMessage" 
                class="send-btn"
                :disabled="!currentMessage.trim() || chatLoading"
              >
                <span v-if="chatLoading">...</span>
                <span v-else>전송</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>

import { generateImageCaption, speakText, stopSpeaking, UserQA, skipSentence, replayPrevious, togglePlayPause} from '@/js/VLM.js';
import ApiService from '@/services/ApiService';
import { DotpadList } from "@/js/dot/DotpadList.js";
import { dotpadState, dotpadTTS, dotpadwebCam } from "@/js/dot/Dotpad.js";
import WhisperService from '@/services/WhisperService.js';
import { connectWS, onWS, sendJSON, isOpen, disconnectWS } from '@/socket/socket.js';
import thinkingSndUrl from '@/assets/sound/thinking.mp3';
import doneSndUrl     from '@/assets/sound/done.mp3'; 


let  labels = [];
let assistant_result = "";

export default {
  name: 'ExcelTest',
  props: {
    id: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      // Excel 파일 업로드 관련 데이터
      imageData: {
        id: '',
        rId: '',
        objectType: '',
        file: '',
        name: '',
        size: null,
        data: null,
        isUploaded: false,
        isAnalyzed: false,
        analyzed: null,
        ctrlDown: false,
        shiftDown: false 
      },
      selectedFile: null,
      uploading: false,
      uploadProgress: 0,
      uploadResult: '',
      uploadError: '',
      isDragOver: false,
      
      // Excel 데이터 관련
      excelData: [],
      headers: [],
      sheetNames: [],
      selectedSheet: '',
      fileMessage: ' ', // 파일과 함께 보낼 메시지
      
      // UI 상태
      analysisLoading: false,
      chatLoading: false,
      currentMessage: '',
      chatMessages: [],
      
      // 기존 분석 결과 (호환성 유지)
      detectedObjects: [],
      dominantColors: [],
      imageCharacteristics: '',
      suggestedQuestions: [
        '이 이미지에서 무엇을 볼 수 있나요?',
        '이미지의 주요 색상은 무엇인가요?',
        '이 이미지가 STEM 교육에 어떻게 활용될 수 있나요?',
        '이미지의 구성 요소를 설명해주세요.'
      ],
      messageIdCounter: 0,
      jsonExpanded: false,

      // STT
      isListening: false,
      streamController: null,
      transcription: '',
      realTimeResults: [],
      sttError: null,
      isInitializing: false,

      // TTS
      ttsEnabled: true,
      rate: 5,
      ours: true,

      // Timers
      elapsed0Ms: 0, timer0Running: false, timer0Interval: null, started0At: null,
      elapsed1Ms: 0, timer1Running: false, timer1Interval: null, started1At: null,
      elapsed2Ms: 0, timer2Running: false, timer2Interval: null, started2At: null,

      // Dotpad
      legend: [],
      chart: [],
      currentDotpadState: "1",
      currentDotpadTTS: "",
      currentDotpadwebCam: false,

      // WS
      wsConnected: false,
      wsStatus: '',
      wsFrameSrc: '',
      wsServerFrame: 0,
      wsAruco: false,
      wsHand: false,
      wsSaveStatus: '대기 중',
      wsSavedRoiSrc: '',
      wsSavedRoiSize: null, // {width,height}
      wsFps: 0,

      // API flags
      apiSending: false,
      apiResult: '',
      apiError: '',
      wsLastRoiSig: '',
      wsLastTipSig: '',
      apiSendingTip: false,
      apiErrorTip: '',
      apiResultTip: '',

      // Tip/ROI pairing (윈도우 기반)
      lastSaveRoiAt: 0, // save_roi 요청 시각(ms)
      roiReadyAt: 0,    // ROI 이미지 준비 시각(ms)
      pendingTip: null, // ROI 준비 전에 도착한 tip 보관

      // Devices
      devices: [],
      selectedDeviceId: "",
      stream: null,
      streamReady: false,

      // Audio
      thinkingAudio: null,
      doneAudio: null,
      _audioUnlocked: false,
      _fadeRAF: null,
      _fadeToken: 0,

      // Output staging flag (printPixels 완료 전까지 thinking 유지)
      outputStaging: false,

      _keyOpts: { capture: true, passive: false },
    };
  },
  created() {
    this.loadImageData();            // ← created에서 호출
    
    // 1. 먼저 localStorage에서 상태 불러오기
    this.loadOursBaselineState();
    this.loadRateState();  // TTS rate 상태 불러오기
    
    // 2. URL 파라미터가 있으면 우선적으로 적용 (새로운 전달값)
    const v = this.$route.query.ours_or_baseline;
    if (v !== undefined) {
      // ours 상태 설정
      this.ours = (v === '1' || v === 'true' || v === true);

      // '0' 또는 'false'면 Baseline 실행
      if (v === '0' || v === 'false' || v === false) {
        this.$nextTick(() => this.selectBaseline());
      } else {
        this.$nextTick(() => this.selectOurs());
      }
    }
  },
  async mounted() {
    //this.loadImageData();
    this.printPixels();
    window.addEventListener('dotpad-state-changed', this.onDotpadStateChanged,  { passive: true } );
    window.addEventListener('dotpad-TTS-changed', this.onDotpadTTSChanged,  { passive: false });
    window.addEventListener('dotpad-webCam-changed', this.onDotpadwebCamChanged,  { passive: false });
    this.currentDotpadState = dotpadState;
    this.currentDotpadTTS = dotpadTTS;
    this.currentDotpadwebCam = dotpadwebCam;
    this._keyOpts = { capture: true, passive: false }; //키보드 여러번 눌림
    // Audio init
    this.thinkingAudio = new Audio(thinkingSndUrl);
    this.thinkingAudio.loop = true;
    this.thinkingAudio.volume = 0.0;

    this.doneAudio = new Audio(doneSndUrl);
    this.doneAudio.volume = 0.18;

    // Unlock audio on user gesture
    const unlock = () => {
      if (this._audioUnlocked) return;
      this._audioUnlocked = true;
      this.thinkingAudio.muted = false;
      this.doneAudio.muted = false;
      this.doneAudio.play().then(() => this.doneAudio.pause()).catch(() => {});
      if (this.isThinking) this.startThinkingSound();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('keydown', unlock, { once: true });

    // Start analysis
    this.processAnalysisData();
    if (this.isThinking) this.startThinkingSound();

    // Devices
    if (!navigator?.mediaDevices?.getUserMedia) {
      console.warn("이 브라우저는 카메라 API를 지원하지 않습니다.");
    } else {
      await this.refreshDevices();
      this._deviceChangeHandler = this.refreshDevices;
      navigator.mediaDevices.addEventListener("devicechange", this._deviceChangeHandler);
    }

    // Whisper init
    try {
      this.isInitializing = true;
      await WhisperService.initialize();
      console.log('✅ Whisper 모델 초기화 완료');
    } catch (error) {
      console.error('❌ Whisper 모델 초기화 실패:', error);
      this.sttError = 'Whisper 모델 초기화 실패: ' + error.message;
    } finally {
      this.isInitializing = false;
    }

    // Shortcuts
    window.addEventListener('keydown', this.handleCtrlShiftDown);
    window.addEventListener('keyup', this.handleCtrlShiftUp);
    window.addEventListener('keydown', this.handleTTSShortcuts, this._keyOpts);
    window.__imageDetailHotkeysBound__ = true;
    
    // WS
    onWS('open', this.handleWSOpen);
    onWS('close', this.handleWSClose);

    let cnt = 0, last = Date.now();
    onWS('message', (data) => {
      if (data?.type !== 'frame') return;

      this.wsFrameSrc = 'data:image/jpeg;base64,' + data.image;
      this.wsServerFrame = data.frame_count || 0;
      this.wsAruco = !!data.aruco_detected;
      this.wsHand = !!data.hand_detected;

      // ROI 이미지 수신
      if (data.saved_image) {
        this.wsSavedRoiSrc = 'data:image/png;base64,' + data.saved_image;

        const probe = new Image();
        probe.onload = () => {
          this.wsSavedRoiSize = { width: probe.width, height: probe.height };
          this.roiReadyAt = Date.now();

          // save_roi 윈도우(3s) 내 + 보류 tip 있으면 전송
          const withinWindow = (this.roiReadyAt - this.lastSaveRoiAt) <= 3000;
          if (withinWindow && this.pendingTip) {
            const tip = this.pendingTip;
            this.pendingTip = null;
            this._sendTipIfNotDup(tip);
          }
        };
        probe.src = this.wsSavedRoiSrc;
      }

      // Tip 좌표 수신: save_roi 윈도우(3s) 내 + ROI 준비됨 조건에서만 전송
      if (data.saved_tip_roi) {
        const tip = Array.isArray(data.saved_tip_roi) && data.saved_tip_roi.length === 2
          ? data.saved_tip_roi
          : (typeof data.saved_tip_roi === 'object' ? [data.saved_tip_roi.x, data.saved_tip_roi.y] : null);

        if (!tip || !Number.isFinite(tip[0]) || !Number.isFinite(tip[1])) return;

        const nowMs = Date.now();
        const withinWindow = (nowMs - this.lastSaveRoiAt) <= 3000; // 3s
        const roiReady = this.wsSavedRoiSize && this.wsSavedRoiSize.width;

        if (!withinWindow) {
          // 창 밖 → 무시
          return;
        }
        if (!roiReady) {
          // ROI 아직이면 보류
          this.pendingTip = tip;
        } else {
          this._sendTipIfNotDup(tip);
        }
      }

      const now = Date.now();
      if (++cnt && now - last >= 1000) { this.wsFps = cnt; cnt = 0; last = now; }
    });

    window.addEventListener('keydown', this.handleArrowDownWS, this._keyOpts);

  },


  beforeUnmount() {
    window.removeEventListener('dotpad-state-changed', this.onDotpadStateChanged);
    window.removeEventListener('dotpad-TTS-changed', this.onDotpadTTSChanged);
    window.removeEventListener('dotpad-webCam-changed', this.onDotpadwebCamChanged);

    try {
      if (this._deviceChangeHandler) {
        navigator.mediaDevices.removeEventListener("devicechange", this._deviceChangeHandler);
      }
    } catch {}
    this.stopStream();


    window.removeEventListener('keydown', this.handleCtrlShiftDown);
    window.removeEventListener('keyup', this.handleCtrlShiftUp);
    window.removeEventListener('keydown', this.handleTTSShortcuts,  this._keyOpts);
    window.__imageDetailHotkeysBound__ = false;
    // 타이머 정리
    if (this.timer0Interval) {
      clearInterval(this.timer0Interval)
    }
    if (this.timer1Interval) {
      clearInterval(this.timer1Interval)
    }
    if (this.timer2Interval) {
      clearInterval(this.timer2Interval)
    }
    // if (this.recognition) {
    //   this.recognition.stop();
    // }
    // this.stopListening();

    disconnectWS();
  },

  // 페이지 이탈 시에도 정지
  beforeRouteLeave(to, from, next) {
    this.cleanupAudioTTS();
    next();
  },

  computed: {
    imageUrl() {
      return this.imageData.file || ''
    },
    imageName() {
      return this.imageData.name || '이미지'
    },
    imageSize() {
      return this.imageData.size || 0
    },
    formattedTime0() {
      const totalSeconds = Math.floor(this.elapsed0Ms / 1000)
      const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
      const ss = String(totalSeconds % 60).padStart(2, '0')
      return `${mm}:${ss}`
    },
    formattedTime1() {
      const totalSeconds = Math.floor(this.elapsed1Ms / 1000)
      const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
      const ss = String(totalSeconds % 60).padStart(2, '0')
      return `${mm}:${ss}`
    },
    formattedTime2() {
      const totalSeconds = Math.floor(this.elapsed2Ms / 1000)
      const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
      const ss = String(totalSeconds % 60).padStart(2, '0')
      return `${mm}:${ss}`
    },

    isThinking() {
      return !!(
        this.isInitializing ||
        this.analysisLoading ||
        this.chatLoading ||
        this.apiSending ||
        this.apiSendingTip ||
        this.outputStaging
      );
    },
  },

  watch: {
    // done 전까지는 자동 정지하지 않음
    isThinking(val, oldVal) {
      if (val && !oldVal) this.startThinkingSound();
    }
  },

  methods: {
    // === 공통 정리 ===
    cleanupAudioTTS() {
      try {
        if (this._fadeRAF) cancelAnimationFrame(this._fadeRAF);
        this._fadeRAF = null;
        this._fadeToken++;

        if (this.thinkingAudio) {
          this.thinkingAudio.pause();
          this.thinkingAudio.currentTime = 0;
          this.thinkingAudio.volume = 0;
        }
        if (this.doneAudio) {
          this.doneAudio.pause();
          this.doneAudio.currentTime = 0;
        }
        stopSpeaking();
      } catch {}
    },

    // Excel 파일 처리 메서드들
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.setSelectedFile(file);
      }
    },

    handleDrop(e) {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0]; // 반드시 files에서 꺼내기
      console.log('B. drop file instanceof File:', file instanceof File); // true
      this.setSelectedFile(file);
    },

    setSelectedFile(file) {
      // 파일 타입 검증
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
      ];
      
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['xlsx', 'xls', 'csv'];
      
      if (!allowedExtensions.includes(fileExtension)) {
        this.uploadError = '지원하지 않는 파일 형식입니다. (.xlsx, .xls, .csv 파일만 업로드 가능)';
        return;
      }
      
      if (file.size > 50 * 1024 * 1024) { // 50MB 제한
        this.uploadError = '파일 크기가 너무 큽니다. (최대 50MB)';
        return;
      }
      
      this.selectedFile = file;
      this.uploadError = '';
      this.uploadResult = '';
      this.uploadProgress = 0;
      console.log('C. state selectedFile instanceof File:', this.selectedFile instanceof File);
    },

    // async uploadFile() {
    //   if (!this.selectedFile) return;
      
    //   this.uploading = true;
    //   this.uploadProgress = 0;
    //   this.uploadError = '';
    //   this.uploadResult = '';
      
    //         try {

    //     ApiService.analyzeExcel(this.selectedFile,this.fileMessage, this.ours ? '1' : '0')
    //       .then(response => {
    //         console.log('AI 분석 결과:', response);
    //         labels = response["resized_list"];
    //         console.log('AI 분석 결과:', labels);
    //         this.chart = this.flattenArray(labels, 60, 40);
    //         this.legend = this.flattenArray(response["legend"], 60, 40);

    //         if (this.ours) {
    //           this.currentDotpadState = "1"
    //           this.printPixels();
    //         }
    //         this.imageData.data = this.chart;
            
    //         this.translate(response["type"])

    //         this.imageData.isAnalyzed = true;
    //         this.imageData.analyzed = response["DS"];
    //         speakText(this.imageData.analyzed, this.rate);

    //         this.imageData.id = response["answer"];
    //         console.log("response[DS]",response["answer"]);
    //       })
    //       .catch(error => {
    //         console.error('AI 분석 실패:', error);
    //       });

    //   } catch (error) {
    //     console.error('❌ 분석 중 오류 발생:', error);
    //     this.imageCharacteristics = '이미지 분석 중 오류가 발생했습니다: ' + error.message;
    //   } finally {
    //     this.analysisLoading = false;
    //   }
    // },

    processExcelData(data) {
      // 서버에서 받은 Excel 데이터 처리
      if (data.sheets) {
        this.sheetNames = Object.keys(data.sheets);
        this.selectedSheet = this.sheetNames[0];
        this.excelData = data.sheets[this.selectedSheet] || [];
        this.headers = this.excelData.length > 0 ? Object.keys(this.excelData[0]) : [];
      }
      
      // 채팅 메시지에 결과 추가
      this.addMessage('system', `Excel 파일을 성공적으로 처리했습니다. (${this.excelData.length}행, ${this.headers.length}열)`);
    },

    clearFile() {
      this.selectedFile = null;
      this.uploadError = '';
      this.uploadResult = '';
      this.uploadProgress = 0;
      this.excelData = [];
      this.headers = [];
      this.sheetNames = [];
      this.fileMessage = ''; // 메시지도 함께 초기화
      this.$refs.fileInput.value = '';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatDate(timestamp) {
      return new Date(timestamp).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // === WS control ===
    connectToServer() { connectWS(); },
    disconnectFromServer() { disconnectWS(); },
    handleArrowDownWS(e) {
      if ((e.code === 'ArrowDown' || e.key === 'j' || e.key === 'J') && isOpen()) {
        e.preventDefault();
        this.lastSaveRoiAt = Date.now(); // 윈도우 시작
        sendJSON({ type: 'save_roi', timestamp: this.lastSaveRoiAt });
        this.wsSaveStatus = '저장 요청됨...';
      }
    },
    handleWSOpen() {
      this.wsConnected = true;
      // 연결 시 과거 상태 정리
      this.wsLastTipSig = '';
      this.wsSavedRoiSize = null;
      this.pendingTip = null;
      this.lastSaveRoiAt = 0;
      this.roiReadyAt = 0;
    },
    handleWSClose() { this.wsConnected = false; },

    // === Tip 전송 ===
    _sendTipIfNotDup(tip) {
      const sig = `${tip[0].toFixed(2)},${tip[1].toFixed(2)}`;
      if (sig === this.wsLastTipSig) return;
      this.wsLastTipSig = sig;
      this.sendTipToApi(tip);
    },

    async sendTipToApi(tip) {
      if (!tip || tip.length < 2) return;
      if (!this.wsSavedRoiSize || !this.wsSavedRoiSize.width) {
        this.pendingTip = tip; // ROI 아직이면 보류
        return;
      }

      this.apiSendingTip = true;
      this.apiErrorTip = '';
      this.apiResultTip = '';

      try {
        const res = await ApiService.sendFingerTip(tip, this.wsSavedRoiSize);

        console.log("res = await ApiService.sendFingerTip", res);
        var text = res['text'];
        var braille = res['braille'];

        // 채팅에 표시할 답변 텍스트 추출
        const answer =
          text?.text ||
          text?.message ||
          text?.answer ||
          (typeof text === 'string' ? text : '좌표 전송이 완료되었습니다.');

        this.addMessage('system', `손가락 좌표를 API로 전송했습니다: ${JSON.stringify(tip)}`);
        this.addMessage('assistant', answer);
        // 상단 상태 텍스트(선택)
        this.apiResultTip = typeof text === 'string' ? text : (text?.message || '전송 완료');
        this.translate(braille);
        
      } catch (e) {
        this.apiErrorTip = e?.message || String(e);
        this.addMessage('system', `손가락 좌표 전송 실패: ${this.apiErrorTip}`);
      } finally {
        this.apiSendingTip = false;
      }
    },


    onDotpadStateChanged(e) {
      console.log("eeeeeeeeeee",e)
      this.currentDotpadState = (e && e.detail && e.detail.state) || dotpadState;
      this.printPixels(); // 상태 변동 즉시 출력
    },
    onDotpadTTSChanged(e) {
      this.currentDotpadTTS = (e && e.detail && e.detail.tts) || dotpadTTS;
      console.log("ddlrpanjdid",this.currentDotpadTTS)
      
      switch (this.currentDotpadTTS) {

        case 'R': // →: 다음 문장으로 건너뛰기
        console.log("ArrowRight");
          e.preventDefault();
          skipSentence(this.rate);
          break;
        case 'L': // →: 다음 문장으로 건너뛰기
        console.log("ArrowLeft");
          e.preventDefault();
          replayPrevious(this.rate);
          break;
        case 'S':                   // S: 즉시 정지
        console.log("KeyS");
          e.preventDefault();
          togglePlayPause(this.rate);
          break;
        default:
          break;
      }
    },
    onDotpadwebCamChanged(e) {
      console.log("webCam",e)
      this.currentDotpadwebCam = (e && e.detail && e.detail.webCam) || dotpadwebCam;
      if (this.currentDotpadwebCam){
        this.lastSaveRoiAt = Date.now(); 
        sendJSON({ type: 'save_roi', timestamp: this.lastSaveRoiAt });
        this.wsSaveStatus = '저장 요청됨...';
      }
    },



    //점자부분 출력
    runTranslate() {
      const text = String(this.tx ?? "");
      // '함수 실행만' 요구: translate가 있으면 실행, 없으면 입력 텍스트 그대로 반환
      this.out = typeof this.translate === "function" ? this.translate(text) : text;

    },
    translate(text) {
      ApiService.translate(text).then(response => {
        let labels = response["text"];
        const dotpadList = DotpadList.getList();
      if (dotpadList.length > 0) {
      for (const dotPad of dotpadList) {
        function padHex(data, totalLen) {
          return data.padEnd(totalLen, "0");
        }

        const filled = padHex(labels, 40);
        // 하단 20셀에 출력
        dotPad.dotpadSDK.dotPadSendModule.setDotPadLineCommand(0, "80", "00", filled);
        dotPad.dotpadSDK.dotPadSendModule.setRefreshMode("TEXT_DISPLAY");
        dotPad.dotpadSDK.dotPadSendModule.sendCommand();
      }
    }

      }).catch(error => {
        console.error('AI 요청 실패:', error);
        this.chatLoading = false;
      });
    },

    printPixels() {
      console.log('🚀 printPixels 호출, state =', this.currentDotpadState);

      // 출력할 픽셀 선택
      let pixels = [];
      const state = this.currentDotpadState ?? dotpadState;
      
      if (state === "1") {
        pixels = this.legend;

        //console.log("📌 legend 출력", this.legend);
      } else if (state === "2") {
        pixels = this.chart;
        //console.log("📊 chart 출력");
      } else {
        console.warn("⚠️ 지원하지 않는 state → 아무 것도 출력하지 않음:", state);
        return;
      }

      // 안전 체크
      if (!Array.isArray(pixels) || pixels.length === 0) {
        console.error('❌ 픽셀 데이터 비어있음:', pixels);
        return;
      }

      const dotpadList = DotpadList.getList();
      if (!Array.isArray(dotpadList) || dotpadList.length === 0) {
        console.warn('⚠️ 연결된 Dotpad 없음');
        return;
      }

      // 전송
      for (const dotPad of dotpadList) {
        try {
          dotPad.dotpadSDK.Load_mapFile(pixels);
          dotPad.dotpadSDK.Make_DTM_Data();
          dotPad.dotpadSDK.sendPixelPattern();
        } catch (err) {
          console.error('Dotpad 전송 오류:', err);
        }
      }
    },

    // 탐색 타이머 관련 메서드 (프론트에서만 작동)
    startTimer0() {
      if (this.timer0Running) return
      this.elapsed0Ms = 0
      this.started0At = Date.now()
      this.timer0Running = true
      this.timer0Interval = setInterval(() => {
        this.elapsed0Ms = Date.now() - this.started0At
      }, 100)
      console.log('🔍 탐색 타이머 시작됨')
    },
    
    stopTimer0() {
      if (!this.timer0Running) return
      clearInterval(this.timer0Interval)
      this.timer0Interval = null
      this.timer0Running = false
      this.elapsed0Ms = Date.now() - (this.started0At || Date.now())
      console.log('🔍 탐색 타이머 종료됨:', this.formattedTime0)
    },
    
    resetTimer0() {
      if (this.timer0Interval) {
        clearInterval(this.timer0Interval)
        this.timer0Interval = null
      }
      this.timer0Running = false
      this.elapsed0Ms = 0
      this.started0At = null
      console.log('🔍 탐색 타이머 초기화됨')
    },

    // 타이머 1 관련 메서드
    startTimer1() {
      if (this.timer1Running) return
      this.elapsed1Ms = 0
      this.started1At = Date.now()
      this.timer1Running = true
      this.timer1Interval = setInterval(() => {
        this.elapsed1Ms = Date.now() - this.started1At
      }, 100)
    },
    
    stopTimer1() {
      if (!this.timer1Running) return
      clearInterval(this.timer1Interval)
      this.timer1Interval = null
      this.timer1Running = false
      this.elapsed1Ms = Date.now() - (this.started1At || Date.now())
      
      // 종료 시에도 서버로 시간 전송
      this.sendTimerToServer(1, "종료")
    },
    
    resetTimer1() {
      if (this.timer1Interval) {
        clearInterval(this.timer1Interval)
        this.timer1Interval = null
      }
      this.timer1Running = false
      this.elapsed1Ms = 0
      this.started1At = null
    },
    
    async stopAndSendTimer1() {
      await this.sendTimerToServer(1, "기록")
    },

    // 타이머 2 관련 메서드
    startTimer2() {
      if (this.timer2Running) return
      this.elapsed2Ms = 0
      this.started2At = Date.now()
      this.timer2Running = true
      this.timer2Interval = setInterval(() => {
        this.elapsed2Ms = Date.now() - this.started2At
      }, 100)
    },
    
    stopTimer2() {
      if (!this.timer2Running) return
      clearInterval(this.timer2Interval)
      this.timer2Interval = null
      this.timer2Running = false
      this.elapsed2Ms = Date.now() - (this.started2At || Date.now())
      
      // 종료 시에도 서버로 시간 전송
      this.sendTimerToServer(2, "종료")
    },
    
    resetTimer2() {
      if (this.timer2Interval) {
        clearInterval(this.timer2Interval)
        this.timer2Interval = null
      }
      this.timer2Running = false
      this.elapsed2Ms = 0
      this.started2At = null
    },
    
    async stopAndSendTimer2() {
      await this.sendTimerToServer(2, "기록")
    },

    // 공통 타이머 전송 메서드
    async sendTimerToServer(timerNumber, action) {
      const seconds = Math.max(0, Math.round(
        timerNumber === 1 ? this.elapsed1Ms / 1000 : this.elapsed2Ms / 1000
      ))
      
      const payload = {
        step_name: `${timerNumber}`,
        timeSpent: seconds,
        request_id: localStorage.getItem('uuid') || null,
      }
      
      try {
        await ApiService.sendTimer(payload)
        console.log(`⏱️ 타이머 ${timerNumber} ${action} 완료:`, payload)
        
        const formattedTime = timerNumber === 1 ? this.formattedTime1 : this.formattedTime2
        this.addMessage('system', `문제 ${timerNumber} ${action}: ${formattedTime} - 서버로 전송 완료`)
      } catch (error) {
        console.error(`⏱️ 타이머 ${timerNumber} ${action} 실패:`, error)
        this.addMessage('system', `타이머 ${timerNumber} ${action} 전송에 실패했습니다.`)
      }
    },

    flattenArray(originalArray, targetWidth, targetHeight) {
      const flattenedArray = new Array(targetWidth * targetHeight).fill(0);

      const inputWidth = originalArray[0].length;
      const inputHeight = originalArray.length;

      const startX = Math.floor((targetWidth - inputWidth) / 2);
      const startY = Math.floor((targetHeight - inputHeight) / 2);

      for (let y = 0; y < inputHeight; y++) {
        for (let x = 0; x < inputWidth; x++) {
          const index = (startY + y) * targetWidth + (startX + x);
          flattenedArray[index] = originalArray[y][x];
        }
      }

      return flattenedArray;
    },
    loadImageData() {
      // URL 쿼리 파라미터에서 ImageData 구조 로드
      const query = this.$route.query
      
      this.imageData = {
        id: this.$route.params.id || query.id || '',
        rId: query.rId || '',
        objectType: query.objectType || '',
        file: query.file || '',
        name: query.name || '이미지',
        size: query.size ? parseInt(query.size) : null,
        isUploaded: query.isUploaded === 'true',
        isAnalyzed: query.isAnalyzed === 'true',
        analyzed: query.analyzed ? JSON.parse(query.analyzed) : null
      };
      const v = query.ours_or_baseline;
      if (v !== undefined) {
        this.ours = (v === true || v === 'true' || v === '1');
      }
      
      console.log('📋 ImageDetail에서 받은 데이터:', this.imageData)
    },
  
    async processAnalysisData() {
      if (!this.selectedFile) return;
      
      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadError = '';
      this.uploadResult = '';


      try {
        // 🔍 이미지 분석 호출
        //const analysisResult = ApiService.Image_Descriptor(imageUrl);
        
        //console.log('🔍 실시간 분석 결과:', analysisResult);

        // 분석 결과 저장

        
        ApiService.analyzeExcel(this.selectedFile,this.fileMessage, this.ours ? '1' : '0')
          .then(response => {
            this.outputStaging = true; // thinking 유지
            console.log('AI 분석 결과:', response);
            labels = response["resized_list"];
            console.log('AI 분석 결과:', labels);
            this.chart = this.flattenArray(labels, 60, 40);
            this.legend = this.flattenArray(response["legend"], 60, 40);

            if (this.ours) {
              this.currentDotpadState = "1"
              this.printPixels();
            }
            this.imageData.data = this.chart;
            
            this.translate(response["type"])
            this.outputStaging = false;

            this.imageData.isAnalyzed = true;
            this.imageData.analyzed = response["DS"];
            this.playDoneThenSpeak("엑셀값을 정리한 데이터입니다. "+response["pandasAI"]+ "추출한 데이터를 기반으로 차트를 만들었습니다. "+this.imageData.analyzed, this.rate);

            console.log("response[DS]",response["DS"]);
            console.log("response[DS]",response["pandasAI"]);
            this.imageData.name = response["pandasAI"]
          })
          .catch(error => {
            console.error('AI 분석 실패:', error);
 
          });

      } catch (error) {
        console.error('❌ 분석 중 오류 발생:', error);
        this.imageCharacteristics = '이미지 분석 중 오류가 발생했습니다: ' + error.message;
        await this.playDoneThenSpeak("정보 추출에 실패 했습니다. 다시 시도해주세요.");
        this.cleanupAudioTTS();
      } finally {
        this.analysisLoading = false;
      }
          
        
    },


// MARK: sendMessage


async sendMessage() {


      const userMessage = this.currentMessage.trim();
      if (!this.currentMessage.trim() || this.chatLoading) return;
      
      this.currentMessage = '';
      // 사용자 메시지 추가
      this.addMessage('user', userMessage);

      this.chatLoading = true;
      console.log('sendChatMessage:', userMessage);
    
      ApiService.sendChatMessage(null, userMessage).then(response => {
        if (!Array.isArray(response.resized_list) || response.resized_list.length === 0) {
          return;
        }
        let labels = response["resized_list"];
        console.log('AI 분석 결과:', labels);
        
        this.chart = this.flattenArray(labels, 60, 40);
        if (this.ours) {
        this.printPixels();}
        this.translate(response['type']);
        let assistant_result = response["text"];
        console.log('assistant_result 분석 결과:', assistant_result);

        // ✅ 응답을 받은 후 메시지를 추가하도록 위치 이동
        this.addMessage('assistant', assistant_result, this.chart);
        this.chatLoading = false;
      }).catch(error => {
         this.playDoneThenSpeak("질문에 대한 정보 추출에 실패 했습니다. 다시 시도해주세요.");
        console.error('AI 요청 실패:', error);
        this.chatLoading = false;
      });

    },


    // async analyzeImage() {
    //   this.analysisLoading = true;
    //   try {
    //     // VLM API를 사용한 이미지 분석
    //     const result = await generateImageCaption(this.imageUrl);
    //     this.imageCharacteristics = result;
    //     this.analysisLoading = false;
    //     // 환영 메시지 추가
    //     this.addMessage('assistant', '이미지 분석 결과: ' + result);
    //   } catch (error) {
    //     this.imageCharacteristics = '이미지 분석 중 오류 발생: ' + error.message;
    //     this.analysisLoading = false;
    //   }
    // },

    // async sendMessage() {
    //   if (!this.currentMessage.trim() || this.chatLoading) return;
    //   const userMessage = this.currentMessage.trim();
    //   this.currentMessage = '';
    //   // 사용자 메시지 추가
    //   this.addMessage('user', userMessage);
    //   this.chatLoading = true;
    //   try {
    //     // VLM API를 사용한 대화형 응답
    //     const response = await generateVLMChatResponse(this.imageUrl, userMessage);
    //     this.addMessage('assistant', response);
    //   } catch (error) {
    //     this.addMessage('assistant', 'AI 응답 오류: ' + error.message);
    //   }
    //   this.chatLoading = false;
    // },

    //AI 대화 
   
    askQuestion(question) {
      this.currentMessage = question
      this.sendMessage()
    },

    addMessage(type, content, labels = null) {
      const message = {
        id: this.messageIdCounter++,
        type,
        content,
        labels,
        timestamp: new Date()
      }
      this.chatMessages.push(message)
      // 자동 TTS: assistant 메시지일 때 자동 재생
      if (this.ttsEnabled && type === 'assistant' && content) {
        this.playDoneThenSpeak(content, this.rate);
      }
      // 메시지 영역 스크롤을 맨 아래로
      this.$nextTick(() => {
        const messagesEl = this.$refs.chatMessages
        if (messagesEl) {
          messagesEl.scrollTop = messagesEl.scrollHeight
        }
      })
    },
    // TTS on/off 토글
    toggleTTS() {
      this.ttsEnabled = !this.ttsEnabled;
      if (!this.ttsEnabled) {
        stopSpeaking();
      }
    },
    selectOurs() {
      this.ours = true;
      // localStorage에 상태 저장
      localStorage.setItem('oursBaselineState', 'ours');
      console.log("✅ Ours 선택됨 및 저장됨");
    },
    selectBaseline() {
      this.ours = false;
      // localStorage에 상태 저장
      localStorage.setItem('oursBaselineState', 'baseline');
      this.playBaseline(); // Baseline 실행
      console.log("✅ Baseline 선택됨 및 저장됨");
    },

    // ours/baseline 상태 불러오기 (localStorage에서)
    loadOursBaselineState() {
      const saved = localStorage.getItem('oursBaselineState');
      if (saved) {
        this.ours = saved === 'ours';
        console.log('📂 저장된 Ours/Baseline 상태 불러옴:', saved);
      }
    },

    // TTS rate 선택 및 저장
    selectRate(num) {
      this.rate = num;
      // localStorage에 rate 상태 저장
      localStorage.setItem('ttsRateState', num.toString());
      console.log('✅ TTS Rate 선택됨 및 저장됨:', num);
    },

    // TTS rate 상태 불러오기 (localStorage에서)
    loadRateState() {
      const saved = localStorage.getItem('ttsRateState');
      if (saved) {
        const rateNum = parseInt(saved);
        // 유효한 rate 값인지 확인 (2, 5, 8, 12, 15 중 하나)
        if ([2, 5, 8, 12, 15].includes(rateNum)) {
          this.rate = rateNum;
          console.log('📂 저장된 TTS Rate 상태 불러옴:', rateNum);
        }
      }
    },
    async playBaseline(){
      try {
        // 동적 import - 빌드 시 asset 포함
        const data = await import(`@/assets/json/${this.imageData.name}.json`);
        this.gridData = data.default; // JSON 데이터가 default로 export됨
        console.log("불러온 JSON 데이터:", this.gridData);
      } catch (error) {
        console.error("JSON 파일을 불러오는 중 오류 발생:", error);
        this.cleanupAudioTTS();
      }
      this.chart = this.flattenArray(this.gridData, 60, 40);
        this.printPixels();

    },
    // 분석 결과 TTS
    playAnalysisTTS() {
      if (!this.imageData.analyzed) return;
      this.outputStaging = true;
      if (this.ours && this.imageData.data) { this.chart = this.imageData.data; this.printPixels(); }
      this.outputStaging = false;
      this.playDoneThenSpeak(this.imageData.analyzed);
    },
     playRagIdTTS() {
      if (!this.imageData.name) return;
      this.playDoneThenSpeak(this.imageData.name);
    },
    // 채팅 답변 TTS
    playChatTTS(message) {
      if (!(message && message.type === 'assistant' && message.content)) return;
      this.outputStaging = true;
      if (message.labels && this.ours) { this.chart = message.labels; this.printPixels(); }
      this.outputStaging = false;
      this.playDoneThenSpeak(message.content);
    },

    goBack() {
      this.cleanupAudioTTS();
      this.$router.push('/')
    },

    formatSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatTime(date) {
      return date.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },

    toggleJsonExpanded() {
      this.jsonExpanded = !this.jsonExpanded
    },

    formatAnalysisData(data) {
      if (!data) return '분석 데이터가 없습니다.'
      
      // JSON을 보기 좋게 포맷팅
      try {
        if (typeof data === 'string') {
          return data
        }
        return JSON.stringify(data, null, 2)
      } catch (error) {
        return '데이터 포맷 오류: ' + String(data)
      }
    },

    handleSpaceDown(e) {
      if (e.code === 'Space' && !this.isListening && !this.chatLoading) {
        e.preventDefault();
        this.startListening();
      }
    },
    handleSpaceUp(e) {
      if (e.code === 'Space' && this.isListening) {
        e.preventDefault();
        this.stopListening();
      }
    },
    handleCtrlShiftDown(e) {
      if (e.ctrlKey && e.shiftKey && !this.isListening && !this.chatLoading) {
        e.preventDefault();
        this.startListening();
      }
    },
    handleCtrlShiftUp(e) {
      if (this.isListening) {
        e.preventDefault();
        this.stopListening();
      }
    },
    async startListening() {
      if (this.isListening || this.chatLoading) return;
      try {
        this.isListening = true;
        this.sttError = null;

        // ✅ 매번 시작 시 리셋
        this.transcription = '';
        this.realTimeResults = [];

        const stream = await WhisperService.getMicrophoneStream();
        this.streamController = await WhisperService.transcribeStream(
          stream,
          (result, error) => {
            if (error) {
              console.error('❌ Whisper 변환 오류:', error);
              this.sttError = '음성 변환 오류: ' + (error.message || error);
              return;
            }

            // ✅ 방어: 콜백 시그니처/포맷 다양성 대비
            let text = '';
            if (typeof result === 'string') text = result;
            else if (result && typeof result.text === 'string') text = result.text;

            if (text && text.trim()) {
              // ✅ 배열인지 재확인 (어딘가에서 덮어썼을 가능성 방지)
              if (!Array.isArray(this.realTimeResults)) this.realTimeResults = [];
              this.realTimeResults.push(text);
              this.transcription = this.realTimeResults.join(' ');
              console.log('🎤 Whisper 결과:', text.trim());
              this.currentMessage = text.trim();
              this.sendMessage();
            }
          },
          { language: 'korean', chunkInterval: 1000 }
        );
      } catch (err) {
        this.sttError = '녹음 시작 오류: ' + (err.message || err);
        this.isListening = false;
      }
    },
    async stopListening() {
      try {
        await this.streamController?.stop?.();
      } catch (e) {
        console.warn('Whisper stop warning:', e);
      } finally {
        this.streamController = null;
        this.isListening = false;
      }

      // ✅ 최종 문장 만들기
      const finalText = (this.realTimeResults || [])
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

      // ✅ 다음을 위해 리셋
      this.realTimeResults = [];
      this.transcription = '';

      console.log('[finalText]', finalText);
      if (finalText) {
        // ✅ currentMessage 의존하지 않고 바로 전송 (UI에도 자동 표시됨)
        this.sendMessage(finalText);
      }
    },
    handleTTSShortcuts(e) {
      // 포커스가 입력창에 있을 땐 무시(원하면 제거)
      if (e.repeat) return;
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
      
      

      if (['input', 'textarea'].includes(tag) || e.isComposing) return;

      switch (e.code) {
        case 'Escape':   //정지
        console.log("Escape");
          stopSpeaking();
          break;
        case 'ArrowRight': // →: 다음 문장으로 건너뛰기
        console.log("ArrowRight");
          e.preventDefault();
          skipSentence(this.rate);
          break;
        case 'ArrowLeft': // →: 다음 문장으로 건너뛰기
        console.log("ArrowLeft");
          e.preventDefault();
          replayPrevious(this.rate);
          break;
        case 'KeyS':                   // S: 즉시 정지
        console.log("KeyS");
          e.preventDefault();
          togglePlayPause(this.rate);
        default:
          break;
      }
    },

    // MediaStream
    stopStream() { try { this.stream?.getTracks()?.forEach(t => t.stop()); } catch {}; this.stream=null; this.streamReady=false; },
    async startStream(deviceId) {
      this.stopStream();
      const constraints = deviceId ? { video: { deviceId: { exact: deviceId } }, audio: false } : { video: true, audio: false };
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.stream = stream; this.streamReady = true;
        await this.refreshDevices();
      } catch (e) { console.error("getUserMedia 실패:", e); this.streamReady = false; }
    },
    async refreshDevices() {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        this.devices = all.filter(d => d.kind === "videoinput");
        if (!this.selectedDeviceId && this.devices.length) this.selectedDeviceId = this.devices[0].deviceId;
      } catch (e) { console.error("enumerateDevices 실패:", e); }
    },
    async restartWithDevice() { if (!this.selectedDeviceId) return; await this.startStream(this.selectedDeviceId); },

    // Thinking loop
    startThinkingSound() {
      try {
        if (!this.thinkingAudio) return;
        this._fadeToken++; const myToken = this._fadeToken;
        if (this._fadeRAF) cancelAnimationFrame(this._fadeRAF);

        this.thinkingAudio.currentTime = 0;
        this.setVolumeSafe(0);

        this.thinkingAudio.play()
          .then(() => {
            const target = 0.08, dur = 180;
            const t0 = performance.now();
            const step = (now) => {
              if (myToken !== this._fadeToken) return;
              const p = Math.max(0, Math.min(1, (now - t0) / dur));
              this.setVolumeSafe(target * p);
              if (p < 1) this._fadeRAF = requestAnimationFrame(step);
            };
            this._fadeRAF = requestAnimationFrame(step);
          })
          .catch(err => console.warn('🔇 thinking play blocked:', err));
      } catch {}
    },
    stopThinkingImmediately() {
      try {
        this._fadeToken++;
        if (this._fadeRAF) cancelAnimationFrame(this._fadeRAF);
        this._fadeRAF = null;
        if (this.thinkingAudio) {
          this.thinkingAudio.pause();
          this.thinkingAudio.currentTime = 0;
          this.thinkingAudio.volume = 0;
        }
      } catch {}
    },
    setVolumeSafe(val) {
      if (!this.thinkingAudio) return;
      const n = Number.isFinite(val) ? val : 0;
      const clamped = Math.max(0, Math.min(1, n));
      this.thinkingAudio.volume = clamped;
    },

    // done → speak (done 직전에 thinking 즉시 정지)
    async playDoneThenSpeak(text) {
      try {
        this.stopThinkingImmediately();
        if (this.doneAudio) {
          this.doneAudio.currentTime = 0;
          await this.doneAudio.play().catch(() => {});
          await new Promise((res) => {
            const h = () => { this.doneAudio.removeEventListener('ended', h); res(); };
            this.doneAudio.addEventListener('ended', h, { once: true });
            setTimeout(res, 400);
          });
        }
      } catch {}
      if (text) speakText(text, this.rate);
    },
    
  }
}
</script>

<style scoped>
.excel-test {
  height: 100vh;
  background: #f4f7fc;
  display: flex;
  flex-direction: column;
}

.detail-header {
  background: white;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  z-index: 100;
}

.back-btn {
  background: #eef2f7;
  color: #333;
  border: 1px solid #dce4f0;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #e1e8f2;
  border-color: #cdd7e5;
}

.detail-header h1 {
  color: #2c3e50;
  margin: 0;
  font-size: 1.5rem;
}

.detail-content {
  display: grid;
  grid-template-columns: minmax(400px, 1fr) 2fr;
  gap: 1.5rem;
  padding: 1.5rem;
  flex-grow: 1;
  overflow: hidden;
}

/* 좌측 Excel 업로드 섹션 */
.upload-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
}

.upload-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.upload-container h2 {
  margin: 0 0 1.5rem 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

/* 파일 업로드 영역 */
.upload-area {
  border: 2px dashed #3b82f6;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  background: #f8faff;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: #2563eb;
  background: #f0f4ff;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
}

.file-input {
  display: none;
}

.upload-label {
  cursor: pointer;
  display: block;
}

.upload-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.8;
}

.upload-label h3 {
  margin: 0.5rem 0;
  color: #374151;
  font-size: 1.1rem;
  font-weight: 600;
}

.upload-hint {
  margin: 0.5rem 0 1rem 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.upload-button {
  display: inline-block;
  background: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.upload-button:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

/* 파일 정보 영역 */
.file-info {
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.file-info h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.file-details p {
  margin: 0.5rem 0;
  color: #495057;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.file-details strong {
  color: #2c3e50;
}

/* 텍스트 입력 영역 */
.message-input-section {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.message-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.message-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dce4f0;
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;
  background: white;
}

.message-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.message-input:disabled {
  background: #f1f3f4;
  cursor: not-allowed;
  opacity: 0.7;
}

.message-hint {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
}

/* 업로드 컨트롤 버튼들 (항상 표시) */
.upload-controls-fixed {
  display: flex;
  gap: 0.75rem;
  margin: 1.5rem 0;
  flex-wrap: wrap;
  justify-content: center;
}

/* 기존 업로드 컨트롤 (사용하지 않음) */
.upload-controls {
  display: flex;
  gap: 0.75rem;
  margin: 1.25rem 0;
  flex-wrap: wrap;
}

.upload-btn {
  flex: 1;
  background: #10b981;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.upload-btn:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.upload-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.clear-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-btn:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
}

.clear-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* 진행률 바 */
.progress-container {
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.progress-text {
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
  min-width: 40px;
}

/* 업로드 결과 */
.upload-result {
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid;
}

.upload-result.success {
  background: #ecfdf5;
  border-color: #10b981;
  color: #065f46;
}

.upload-result.error {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}

.upload-result h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
}

.upload-result p {
  margin: 0;
  font-size: 0.9rem;
}

.image-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.image-wrapper {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f9fa;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  border-radius: 8px;
  transition: transform 0.3s ease;
}

.main-image:hover {
  transform: scale(1.02);
}

.image-info {
  margin-top: auto;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  flex-shrink: 0;
}

.image-info h3 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 600;
}

.image-info p {
  margin: 0.25rem 0;
  color: #6c757d;
  font-size: 0.85rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ImageData 상세 정보 스타일 */
.imagedata-details {
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e3f2fd;
}

.imagedata-details h4 {
  margin: 0 0 0.75rem 0;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
}

/* 우측 분석 섹션 */
.analysis-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  min-height: 0; /* Flex 아이템이 넘칠 때 축소되도록 허용 */
}

.analysis-panel {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  overflow-y: auto;
  max-height: 45%; /* 높이 비율 조정 */
}

.analysis-panel h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.loading {
  text-align: center;
  color: #7f8c8d;
  padding: 2rem;
}

.analysis-results {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.analysis-item strong {
  color: #2c3e50;
  display: block;
  margin-bottom: 0.5rem;
}

.analysis-item ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #555;
}

.color-palette {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.color-swatch {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid #ddd;
  cursor: pointer;
}

/* 채팅 패널 */
.chat-panel {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.chat-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.chat-panel h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.chat-messages {
  flex-grow: 1;
  overflow-y: auto;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  scrollbar-width: thin;
  scrollbar-color: #dce4f0 #f8f9fa;
}

.chat-messages::-webkit-scrollbar {
  width: 10px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f3f4;
  border-radius: 5px;
  margin: 5px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 5px;
  border: 2px solid #f1f3f4;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #9aa0a6;
}

.message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 12px;
  max-width: 85%;
  word-wrap: break-word;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.user {
  background: #3b82f6;
  color: white;
  margin-left: auto;
  text-align: right;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

.message.assistant {
  background: #eef2f7;
  color: #333;
  border: 1px solid #e2e8f0;
  box-shadow: none;
}

.message.system {
  background: #e8f5e8;
  color: #2d5016;
  border: 1px solid #c3e6c3;
  font-style: italic;
  max-width: 100%;
  text-align: center;
}

.empty-chat-message {
  text-align: center;
  color: #6c757d;
  font-size: 0.9rem;
  padding: 2rem;
  line-height: 1.6;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  margin: 1rem 0;
}

.message-content {
  margin-bottom: 0.25rem;
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
}

.chat-input-area {
  flex-shrink: 0; /* 입력 영역은 줄어들지 않도록 설정 */
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.input-container {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.chat-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #dce4f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  background: white;
  resize: none;
}

.chat-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.send-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  background: #2563eb;
}

.send-btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.suggested-questions {
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.suggested-questions p {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  color: #495057;
  font-weight: 600;
  text-align: left;
}

.suggested-questions-container {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.25rem 0;
  scrollbar-width: thin;
  scrollbar-color: #bdc3c7 #ecf0f1;
}

.suggested-questions-container::-webkit-scrollbar {
  height: 6px;
}

.suggested-questions-container::-webkit-scrollbar-track {
  background: #ecf0f1;
  border-radius: 3px;
}

.suggested-questions-container::-webkit-scrollbar-thumb {
  background: #bdc3c7;
  border-radius: 3px;
}

.suggestion-btn {
  background: #eef2f7;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 16px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.suggestion-btn:hover {
  background: #e2e8f0;
  border-color: #dce4f0;
  color: #2d3748;
}

/* 반응형 */
/* 반응형 디자인 */
@media (max-width: 1200px) {
  .detail-content {
    grid-template-columns: 450px 1fr;
    gap: 1rem;
  }
}

@media (max-width: 1024px) {
  .detail-content {
    grid-template-columns: 1fr;
    height: auto;
    padding: 1rem;
  }
  
  .image-section {
    order: 1;
    margin-bottom: 1rem;
    height: auto; /* 높이 자동 조정 */
  }
  
  .analysis-section {
    order: 2;
    height: auto; /* 높이 자동 조정 */
  }
  
  .image-wrapper {
    max-height: 50vh; /* 화면 높이에 비례하여 조정 */
  }
  
  .timer-container {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .timer-section {
    min-width: auto;
  }
  
  .chat-panel {
    height: 60vh; /* 화면 높이에 비례하여 조정 */
  }
}

/* 새로운 ImageData 관련 스타일 */
.analysis-status {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: center;
}

.analysis-status:first-child {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: #f8f9fa;
  color: #6c757d;
}

.status-badge.uploaded {
  background: #d4edda;
  color: #155724;
}

.status-badge.analyzed {
  background: #e2e3f7;
  color: #383d41;
}

/* imagedata-info 스타일 제거됨 - imagedata-details로 통합 */

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.info-item {
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  font-size: 0.9rem;
}

.ai-analysis {
  margin-bottom: 1.5rem;
}

.analysis-json-container {
  position: relative;
}

.json-toggle-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: #4a5568;
  color: white;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  z-index: 1;
  transition: background 0.3s ease;
}

.json-toggle-btn:hover {
  background: #2d3748;
}

.analysis-json {
  background: #2d3748;
  color: #e2e8f0;
  padding: 1rem;
  padding-top: 2.5rem;
  border-radius: 8px;
  font-size: 0.8rem;
  overflow-x: auto;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.4;
  transition: max-height 0.3s ease;
}

.analysis-json.expanded {
  max-height: 600px;
}

@media (max-width: 768px) {
  .detail-header {
    padding: 1rem;
  }
  
  .detail-content {
    padding: 0.75rem;
    gap: 0.75rem;
  }
  
  .analysis-panel,
  .chat-panel {
    padding: 1rem;
  }
  
  .analysis-status {
    flex-direction: column;
    align-items: stretch;
  }
  
  .status-btn {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
  
  .timer-container {
    padding: 0.5rem;
  }
  
  .timer-section {
    margin-bottom: 0.5rem;
  }
  
  .timer-section-wrapper {
    padding: 0.75rem;
    margin: 0.5rem 0;
  }
  
  .timer-section-title {
    font-size: 0.9rem;
  }
  
  .chat-messages {
    min-height: 150px;
    max-height: 250px;
  }
  
  .chat-panel {
    min-height: 400px;
    height: auto;
  }
  
  .chat-input-area {
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 2px solid #e9ecef;
    padding: 1rem;
    margin: 0;
  }
  
  .suggested-questions {
    padding: 0.75rem;
    margin-top: 0.5rem;
  }
  
  .suggested-questions-container {
    gap: 0.3rem;
  }
  
  .suggestion-btn {
    padding: 0.5rem 0.8rem;
    font-size: 0.8rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .model-toggle-container {
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
  }
  
  .toggle-label {
    text-align: center;
  }
  
  .toggle-switch-wrapper {
    width: 100%;
    max-width: 250px;
  }
  
  /* 좌우배치 컨테이너 반응형 */
  .controls-horizontal-container {
    flex-direction: column;
    gap: 0.75rem;
    margin: 0.75rem 0;
  }
  
  /* TTS Rate 반응형 스타일 */
  .tts-rate-wrapper {
    padding: 1rem;
    margin: 0;
    flex: none;
  }
  
  .tts-rate-title {
    font-size: 0.9rem;
    padding-bottom: 0.5rem;
  }
  
  .tts-rate-container {
    flex-direction: row;
    gap: 0.75rem;
    align-items: center;
    justify-content: center;
  }
  
  .rate-label {
    text-align: center;
    font-size: 0.85rem;
  }
  
  .rate-buttons-wrapper {
    gap: 0.4rem;
    justify-content: center;
  }
  
  .rate-btn {
    width: 40px;
    height: 40px;
    font-size: 0.8rem;
  }
  
  .rate-btn:hover {
    transform: translateY(-1px) scale(1.03);
  }
  
  .rate-btn.active {
    transform: scale(1.05);
  }
  
  /* 모델 선택 반응형 스타일 */
  .model-selection-wrapper {
    margin: 0;
    flex: none;
  }
}

.stt-indicator {
  background: #e3f2fd;
  color: #1976d2;
  padding: 8px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  font-weight: bold;
  text-align: center;
}
.error {
  background: #ffebee;
  color: #c62828;
  padding: 8px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  text-align: center;
}

.tts-toggle-btn {
  background: #f1f3f4;
  color: #2c3e50;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s ease;
  align-self: flex-start;
}

.tts-toggle-btn:hover {
  background: #e2e6ea;
}

/* 기존 status-btn은 다른 용도로 유지 */
.status-btn {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  margin-right: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  min-width: 60px;
  text-align: center;
}

.status-btn:hover {
  background: #2980b9;
  transform: translateY(-1px);
}

.status-btn.active {
  background: #2ecc71;
  box-shadow: 0 2px 8px rgba(46, 204, 113, 0.3);
}

/* 좌우배치 컨테이너 */
.controls-horizontal-container {
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
  margin: 0.75rem 0;
}

/* TTS 속도 선택 영역 스타일 */
.tts-rate-wrapper {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: 1px solid #e3f2fd;
  border-radius: 12px;
  padding: 0.75rem;
  margin: 0;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
  flex: 1;
}

.tts-rate-title {
  margin: 0 0 0.5rem 0;
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 0.4rem;
}

.tts-rate-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  flex-direction: row;
}

.rate-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  min-width: fit-content;
}

.rate-buttons-wrapper {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.rate-btn {
  background: rgba(255, 255, 255, 0.9);
  color: #4a5568;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  width: 45px;
  height: 45px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.rate-btn:hover {
  background: white;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border-color: #3b82f6;
}

.rate-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #2563eb;
  transform: translateY(-1px) scale(1.1);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  animation: pulse-rate 1.5s ease-in-out infinite alternate;
}

@keyframes pulse-rate {
  from { 
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }
  to { 
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.6);
  }
}

/* 모델 선택 영역 스타일 */
.model-selection-wrapper {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.75rem;
  margin: 0;
  flex: 1;
}

.model-selection-title {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.4rem;
}

.model-toggle-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.toggle-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  min-width: fit-content;
}

.toggle-switch-wrapper {
  display: flex;
  background: #e9ecef;
  border-radius: 25px;
  padding: 3px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.toggle-option {
  border: none;
  background: transparent;
  color: #6c757d;
  padding: 0.5rem 1.25rem;
  border-radius: 22px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  min-width: 80px;
  text-align: center;
}

.toggle-option:hover {
  color: #495057;
}

.toggle-option.active {
  background: #3b82f6;
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  transform: translateY(-1px);
}

.toggle-option:first-child.active {
  background: #10b981;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.toggle-option:last-child.active {
  background: #f59e0b;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

/* 타이머 섹션 래퍼 */
.timer-section-wrapper {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.75rem;
  margin: 0.75rem 0;
}

.timer-section-title {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.4rem;
}

/* 타이머 스타일 */
.timer-container {
  display: flex;
  gap: 0.6rem;
  background: white;
  border: 1px solid #e3f2fd;
  border-radius: 8px;
  padding: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
}

.timer-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  flex: 1;
  min-width: 120px;
}

.timer-label {
  font-size: 0.8rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 0.3rem;
  text-align: center;
}

.timer-display {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.3rem;
}

.timer-icon {
  font-size: 1rem;
}

.timer-time {
  font-size: 1rem;
  font-weight: bold;
  color: #2c3e50;
  font-family: 'Monaco', 'Consolas', monospace;
  min-width: 50px;
  text-align: center;
}

.timer-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #bdc3c7;
  transition: all 0.3s ease;
}

.timer-status.running {
  background: #e74c3c;
  animation: pulse 1.5s ease-in-out infinite alternate;
}

@keyframes pulse {
  from { opacity: 1; }
  to { opacity: 0.5; }
}

.timer-controls {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.timer-btn {
  border: 1px solid #dce4f0;
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: #333;
}

.timer-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8f9fa;
}

.start-btn {
  background: #e3f2fd;
  border-color: #bbdefb;
  color: #1e88e5;
}

.start-btn:hover:not(:disabled) {
  background: #dbeffd;
}

.record-btn {
  background: #e8f5e9;
  border-color: #c8e6c9;
  color: #388e3c;
}

.record-btn:hover:not(:disabled) {
  background: #ddf2de;
}

.stop-btn {
  background: #fff3e0;
  border-color: #ffcc80;
  color: #ef6c00;
}

.stop-btn:hover:not(:disabled) {
  background: #ffecc1;
}

.reset-btn {
   background: #fbe9e7;
  border-color: #ffccbc;
  color: #d84315;
}

.reset-btn:hover:not(:disabled) {
  background: #f9e2de;
}

.ws-row { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:12px; }
.ws-col { flex:1 1 320px; background:#f8f9fa; border:1px solid #e9ecef; border-radius:12px; padding:10px; }
.ws-title { margin:0 0 8px 0; font-size:.95rem; color:#2c3e50; }
.ws-frame { position:relative; background:white; border:1px solid #e3f2fd; border-radius:8px; min-height:180px; display:flex; align-items:center; justify-content:center; overflow:hidden; }
.ws-img { max-width:100%; max-height:320px; }
.ws-empty { color:#9aa0a6; font-size:.9rem; padding:20px; }
.ws-fps { position:absolute; top:8px; right:8px; font-size:.75rem; background:rgba(0,0,0,.7); color:#fff; padding:2px 6px; border-radius:6px; }
.ws-stats { margin-top:6px; display:flex; gap:8px; flex-wrap:wrap; font-size:.85rem; color:#4a5568; }

.header-actions {
  margin-left: auto;               /* 우측 정렬 */
  display: flex;
  align-items: center;
  gap: 8px;
}

.ws-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 12px;
  background: #f5f7fb;
  border: 1px solid #e5e9f2;
  font-size: 12px;
  color: #475569;
}

.ws-indicator .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;            /* 기본: 회색(해제) */
  box-shadow: 0 0 0 2px rgba(0,0,0,0.04) inset;
}

.ws-indicator.on .dot {
  background: #22c55e;            /* 연결: 초록 */
}

.connect-btn {
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #dce4f0;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  transition: background .2s, transform .05s;
}

.connect-btn:hover { background: #f8fafc; }
.connect-btn:active { transform: translateY(1px); }

</style>


